import * as React from 'react';
import { connect } from 'react-redux';

import Page from 'components/page/Page';

import DiscoveryCreatorsContext from '../context/DiscoveryCreatorsContext';

import DiscoveryCreatorsFilters from 'modules/discovery/creators/components/filters/DiscoveryCreatorsFilters';
import DiscoveryCreatorsResults from 'modules/discovery/creators/components/results/DiscoveryCreatorsResults';

import { danger, success } from 'redux-store/reducers/common-reducer';
import {
  fetchDiscoveryCreators,
  fetchDiscoveryTaxonomies,
  resetDiscoveryCreators,
  resetFilters as resetDiscoveryFilters
} from 'modules/discovery/creators/reducers/creators';
import {
  fetchMyCreatedProfileSmartlist,
  resetMyCreatedProfileSmartlistId,
  saveCreatorsToList
} from 'modules/lists/reducers/lists';

import { __t, applyTranslations } from 'utils/language';
import { getDiscoverySubnavigation } from 'utils/navigation';
import { isManagedServiceSubscription } from 'utils/subscription';
import { removeItem } from 'utils/storage/sessionStorage';
import { insertIf } from 'utils/list';
import { HAS_MANUALLY_SELECTED_CHANNEL_STORAGE_NAME } from '../utils/creators';

// freemium "static" tile
import staticTile from 'modules/freemium/static-profile/profile.json';

import './discovery-creators-page.scss';

import type {
  DiscoveryCreatorsFilters as DiscoveryCreatorsFiltersType,
  DiscoveryFilterOptions,
  InfluencerTile,
  Organization
} from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./discovery-creators-page.i18n.json'));
applyTranslations(require('components/creator-tile/save-to-collection/creator-tile-save-to-collection.i18n.json'));

type StateProps = {
  discoveryCreatorsResults: Array<InfluencerTile>,
  discoveryCreatorsFilters: DiscoveryCreatorsFiltersType,
  isLoadingDiscoveryCreators: boolean,
  isSavingToList: boolean,
  currentOrganization: Organization
};

type DispatchProps = {
  dispatchFetchDiscoveryCreators: (a: DiscoveryFilterOptions) => void,
  dispatchResetDiscoveryCreators: () => void,
  dispatchResetDiscoveryFilters: () => void,
  dispatchFetchMyCreatedProfileSmartlist: (a: number) => void,
  dispatchResetMyCreatedProfileSmartlistId: () => void,
  dispatchSaveCreatorsToList: (
    e: string,
    d: string,
    c: number,
    b: DiscoveryCreatorsFiltersType,
    a: string
  ) => Promise<any>,
  dispatchSaveCreatorsToListNotification: (b: string, a: string) => void,
  dispatchFetchTaxonomies: () => Promise<void>
};

type Props = StateProps & DispatchProps;

const msgKey = 'discovery_creators_page';
const saveToCollectionMsgKey = 'creator_tile_save_to_collection';

class DiscoveryCreatorsPage extends React.PureComponent<Props> {
  componentDidMount() {
    const { currentOrganization, dispatchFetchTaxonomies, dispatchFetchMyCreatedProfileSmartlist } = this.props;
    const orgId = currentOrganization.id;

    removeItem(HAS_MANUALLY_SELECTED_CHANNEL_STORAGE_NAME);

    dispatchFetchTaxonomies();

    this.handleFetchDiscoveryCreators();

    dispatchFetchMyCreatedProfileSmartlist(orgId);
  }

  componentDidUpdate(prevProps: Props) {
    const { currentOrganization, dispatchFetchMyCreatedProfileSmartlist } = this.props;
    const orgId = currentOrganization.id;

    if (prevProps.currentOrganization && currentOrganization.id !== prevProps.currentOrganization.id) {
      dispatchFetchMyCreatedProfileSmartlist(orgId);
    }
  }

  componentWillUnmount() {
    const {
      dispatchResetDiscoveryCreators,
      dispatchResetMyCreatedProfileSmartlistId,
      dispatchResetDiscoveryFilters
    } = this.props;

    dispatchResetDiscoveryCreators();
    dispatchResetDiscoveryFilters();
    dispatchResetMyCreatedProfileSmartlistId();
  }

  handleFetchDiscoveryCreators = (page?: number | null): void => {
    const { dispatchFetchDiscoveryCreators } = this.props;
    const updatedPage = page ? { page } : {};

    dispatchFetchDiscoveryCreators(updatedPage);
  };

  handleSaveCreatorsToList = (
    label: string,
    description: string,
    orgId: number,
    filters: DiscoveryCreatorsFiltersType,
    sort: string
  ): void => {
    const { dispatchSaveCreatorsToList, dispatchSaveCreatorsToListNotification } = this.props;
    const errorMessage = __t(`${saveToCollectionMsgKey}.messages.error`);

    dispatchSaveCreatorsToList(label, description, orgId, filters, sort)
      .then(results => {
        const listId = results?.payload?.data?.id;

        if (listId) {
          const message = __t(`${saveToCollectionMsgKey}.messages.success`, {
            collectionUrl: `/collections/creators/${listId}?prev=lists&orgId=${orgId}`
          });

          dispatchSaveCreatorsToListNotification(message, 'success');
        } else {
          dispatchSaveCreatorsToListNotification(errorMessage, 'danger');
        }
      });
  };

  renderDiscoveryResults(): React.ReactNode {
    const {
      currentOrganization,
      discoveryCreatorsFilters,
      discoveryCreatorsResults,
      isLoadingDiscoveryCreators,
      isSavingToList
    } = this.props;

    const isFreemium = currentOrganization?.isFreemium || false;

    return (
      <DiscoveryCreatorsContext.Provider
        value={{
          isManagedService: isManagedServiceSubscription(currentOrganization)
        }}
      >
        <DiscoveryCreatorsFilters
          filters={discoveryCreatorsFilters}
          isFreemium={isFreemium}
        />
        <DiscoveryCreatorsResults
          creators={[
            ...insertIf(isFreemium, staticTile), // COLLAB-12525
            ...discoveryCreatorsResults
          ]}
          isLoading={isLoadingDiscoveryCreators}
          isSavingToList={isSavingToList}
          isFreemium={isFreemium}
          onFetchCreators={this.handleFetchDiscoveryCreators}
          onSaveCreatorsToList={this.handleSaveCreatorsToList}
        />
      </DiscoveryCreatorsContext.Provider>
    );
  }

  render(): React.ReactNode {
    const { currentOrganization } = this.props;

    return (
      <Page
        documentTitle={`${__t(`${msgKey}.browser_title`)}`}
        subnavigation={getDiscoverySubnavigation(currentOrganization.id)}
      >
        <section className="discovery-creators-page">
          {this.renderDiscoveryResults()}
        </section>
      </Page>
    );
  }
}

const mapStateToProps = ({ organizations, lists, discoveryCreators }): StateProps => ({
  discoveryCreatorsResults: discoveryCreators.results,
  discoveryCreatorsFilters: discoveryCreators.filters,
  isLoadingDiscoveryCreators: discoveryCreators.isLoading,
  currentOrganization: organizations.currentOrganization,
  isSavingToList: lists.isSavingToList
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchMyCreatedProfileSmartlist: (orgId: number) => (
    dispatch(fetchMyCreatedProfileSmartlist(orgId))
  ),

  dispatchResetMyCreatedProfileSmartlistId: () => (
    dispatch(resetMyCreatedProfileSmartlistId())
  ),

  dispatchFetchDiscoveryCreators: (options: DiscoveryFilterOptions) => (
    dispatch(fetchDiscoveryCreators(options))
  ),

  dispatchFetchTaxonomies: (): Promise<void> => dispatch(fetchDiscoveryTaxonomies()),
  dispatchResetDiscoveryCreators: () => dispatch(resetDiscoveryCreators()),

  dispatchSaveCreatorsToList: (label: string, description: string, orgId: number, filters: DiscoveryCreatorsFiltersType, sort: string) => (
    dispatch(saveCreatorsToList(label, description, orgId, filters, sort))
  ),

  dispatchSaveCreatorsToListNotification: (message: string, type: string) => (
    type === 'success' ? dispatch(success(message)) : dispatch(danger(message))
  ),

  dispatchResetDiscoveryFilters: () => (
    dispatch(resetDiscoveryFilters())
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(DiscoveryCreatorsPage);
